import {
  Bell,
  ChevronRight,
  CircleDot,
  Clock3,
  ExternalLink,
  Filter,
  Flame,
  Gamepad2,
  Menu,
  Newspaper,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  Tag,
  X,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL, getFacets, getFeed, getSources } from "./api";
import { LocaleToggle, useLocale, type DisplayLocale } from "./locale";
import type { Facets, FeedEvent, FeedTab, SourceHealth } from "./types";

const EMPTY_FACETS: Facets = { platforms: [], contentTypes: [], tags: [] };

const TAB_CONFIG: Array<{ id: FeedTab; icon: typeof Newspaper }> = [
  { id: "following", icon: Sparkles },
  { id: "latest", icon: Newspaper },
  { id: "updates", icon: RefreshCw },
  { id: "reviews", icon: Star },
  { id: "deals", icon: Tag },
];

const TAB_LABELS: Record<DisplayLocale, Record<FeedTab, string>> = {
  en: { following: "Following", latest: "Latest", updates: "Updates", reviews: "Reviews", deals: "Deals" },
  "zh-Hans": { following: "关注", latest: "最新", updates: "更新", reviews: "评测", deals: "折扣" },
};

const APP_COPY = {
  en: {
    openMenu: "Open menu",
    home: "GameNews home",
    submitSearch: "Submit search",
    search: "Search",
    searchPlaceholder: "Search games, platforms, or events",
    clearSearch: "Clear search",
    notifications: "Notifications",
    channels: "My channels",
    closeMenu: "Close menu",
    channelNav: "News channels",
    interests: "My interests",
    railFooter: "Powered by multi-source event aggregation",
    intelligence: "intelligence",
    followingDescription: "Curated from the platforms you follow",
    latestDescription: "Live signals from official, media, and community sources",
    filter: "Filters",
    platform: "Platform",
    allPlatforms: "All platforms",
    resetFilters: "Reset filters",
    onboardingTitle: "Build your personal game intelligence feed",
    onboardingBody: "Choose the platforms you follow and we will keep only matching events.",
    syncing: "Syncing",
    events: "events",
    refresh: "Refresh",
    apiErrorTitle: "The intelligence API is temporarily unavailable",
    loadError: "Unable to load news",
    retry: "Try again",
    loading: "Loading",
    noMatches: "No events match your current interests",
    collecting: "The first sources are being collected",
    noMatchesBody: "Try changing your interests or clearing the filters.",
    collectingBody: "The ingestion Worker is turning official and media feeds into event cards.",
    viewAll: "View all news",
    sourceRadar: "Source radar",
    realtime: "Live",
    connectedSources: "sources",
    enabledSources: "enabled",
    latestSources: "Latest sources",
    all: "All",
    items: "items",
    sourcesEmpty: "Sources will appear after the database is initialized.",
    learningTitle: "An aggregator that is still learning",
    learningBody: "Every card keeps its source. Deduplication, translations, and interest recommendations will improve over time.",
    justNow: "just now",
    minutesAgo: "min ago",
    hoursAgo: "hr ago",
    daysAgo: "days ago",
    sources: "sources",
    sourcePending: "Source pending",
    readOriginal: "Read original",
  },
  "zh-Hans": {
    openMenu: "打开菜单",
    home: "GameNews 首页",
    submitSearch: "提交搜索",
    search: "搜索",
    searchPlaceholder: "搜索游戏、平台或事件",
    clearSearch: "清空搜索",
    notifications: "通知",
    channels: "我的频道",
    closeMenu: "关闭菜单",
    channelNav: "新闻频道",
    interests: "我的关注",
    railFooter: "由多来源事件聚合驱动",
    intelligence: "情报",
    followingDescription: "根据你选择的平台整理",
    latestDescription: "来自官方、媒体与社区信号的实时聚合",
    filter: "筛选",
    platform: "平台",
    allPlatforms: "全部平台",
    resetFilters: "重置筛选",
    onboardingTitle: "建立你的专属游戏情报流",
    onboardingBody: "选择你关注的平台，我们只保留与这些平台有关的事件。",
    syncing: "同步中",
    events: "条事件",
    refresh: "刷新",
    apiErrorTitle: "暂时无法连接情报 API",
    loadError: "无法加载新闻",
    retry: "重新尝试",
    loading: "正在加载",
    noMatches: "当前关注条件没有匹配事件",
    collecting: "第一批来源正在采集中",
    noMatchesBody: "试试调整关注项或清除筛选。",
    collectingBody: "采集 Worker 会把官方和媒体 RSS 逐步整理为事件卡片。",
    viewAll: "查看全部新闻",
    sourceRadar: "来源雷达",
    realtime: "实时",
    connectedSources: "接入来源",
    enabledSources: "已启用",
    latestSources: "最新来源",
    all: "全部",
    items: "条内容",
    sourcesEmpty: "来源清单将在数据库初始化后显示。",
    learningTitle: "这是一个正在学习的聚合器",
    learningBody: "每张卡片都保留出处。事件去重、翻译和兴趣推荐会逐步完善。",
    justNow: "刚刚",
    minutesAgo: "分钟前",
    hoursAgo: "小时前",
    daysAgo: "天前",
    sources: "个来源",
    sourcePending: "来源整理中",
    readOriginal: "查看原文",
  },
} as const;

const SUPPORTED_INTERESTS = ["Nintendo", "PlayStation", "Xbox", "PC / Steam"] as const;
const SUPPORTED_INTEREST_SET = new Set<string>(SUPPORTED_INTERESTS);

function timeAgo(timestamp: number, locale: DisplayLocale): string {
  const copy = APP_COPY[locale];
  if (!timestamp) return copy.justNow;
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return copy.justNow;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} ${copy.minutesAgo}`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} ${copy.hoursAgo}`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)} ${copy.daysAgo}`;
  return new Intl.DateTimeFormat(locale === "zh-Hans" ? "zh-CN" : "en", { month: "short", day: "numeric" }).format(timestamp);
}

function contentTypeLabel(type: string, locale: DisplayLocale): string {
  const labels: Record<DisplayLocale, Record<string, string>> = {
    en: {
      announcement: "Announcement", news: "News", release: "Release", delay: "Delay",
      update: "Update", patch: "Patch", patch_notes: "Patch", system_update: "System update",
      deal: "Deal", rumor: "Rumor", review: "Review",
    },
    "zh-Hans": {
      announcement: "公告", news: "新闻", release: "发售", delay: "延期",
      update: "更新", patch: "补丁", patch_notes: "补丁", system_update: "系统更新",
      deal: "折扣", rumor: "传闻", review: "评测",
    },
  };
  return labels[locale][type] ?? type;
}

function readStoredInterests(): string[] {
  try {
    const value = window.localStorage.getItem("gamenews.interests");
    if (!value) return [];
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter(
        (item): item is string => typeof item === "string" && SUPPORTED_INTEREST_SET.has(item),
      )
      : [];
  } catch {
    return [];
  }
}

function App() {
  const { locale } = useLocale();
  const copy = APP_COPY[locale];
  const tabLabels = TAB_LABELS[locale];
  const [activeTab, setActiveTab] = useState<FeedTab>("latest");
  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [facets, setFacets] = useState<Facets>(EMPTY_FACETS);
  const [sources, setSources] = useState<SourceHealth[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<string[]>(readStoredInterests);
  const [platform, setPlatform] = useState("");
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const feedRequestGeneration = useRef(0);

  const interestOptions = SUPPORTED_INTERESTS;

  const loadFeed = useCallback(async (signal?: AbortSignal) => {
    const generation = ++feedRequestGeneration.current;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ limit: "30", locale });
    if (platform) params.set("platform", platform);
    if (query) params.set("q", query);
    if (activeTab === "updates") params.set("content_type", "update");
    if (activeTab === "reviews") params.set("event_type", "review");
    if (activeTab === "deals") params.set("content_type", "deal");

    try {
      const response = await getFeed(params, signal);
      if (generation !== feedRequestGeneration.current) return;
      setEvents(response.items);
    } catch (requestError) {
      if (
        generation !== feedRequestGeneration.current ||
        (requestError instanceof DOMException && requestError.name === "AbortError")
      ) return;
      setError(requestError instanceof Error ? requestError.message : copy.loadError);
    } finally {
      if (!signal?.aborted && generation === feedRequestGeneration.current) {
        setLoading(false);
      }
    }
  }, [activeTab, copy.loadError, locale, platform, query]);

  useEffect(() => {
    setEvents([]);
  }, [locale]);

  useEffect(() => {
    const controller = new AbortController();
    void loadFeed(controller.signal);
    return () => controller.abort();
  }, [loadFeed]);

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([getFacets(controller.signal), getSources(controller.signal)])
      .then(([facetData, sourceData]) => {
        setFacets(facetData);
        setSources(sourceData);
      })
      .catch((requestError: unknown) => {
        if (!(requestError instanceof DOMException && requestError.name === "AbortError")) {
          console.warn("Could not load secondary data", requestError);
        }
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    window.localStorage.setItem("gamenews.interests", JSON.stringify(selectedInterests));
  }, [selectedInterests]);

  const visibleEvents = useMemo(() => {
    if (activeTab !== "following" || selectedInterests.length === 0) return events;
    const interests = selectedInterests.map((item) => item.toLocaleLowerCase());
    return events.filter((event) => {
      const haystack = [...event.platforms, ...event.tags, ...event.games, event.contentType, event.eventType]
        .join(" ")
        .toLocaleLowerCase();
      return interests.some((interest) => haystack.includes(interest));
    });
  }, [activeTab, events, selectedInterests]);

  const toggleInterest = (interest: string) => {
    setSelectedInterests((current) => current.includes(interest)
      ? current.filter((item) => item !== interest)
      : [...current, interest]);
  };

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setQuery(draftQuery.trim());
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="icon-button mobile-menu" onClick={() => setMobileMenuOpen(true)} aria-label={copy.openMenu}>
          <Menu size={20} />
        </button>
        <a className="brand" href="/" aria-label={copy.home}>
          <span className="brand-mark"><Gamepad2 size={19} /></span>
          <span className="brand-word">GAME<span>//</span>NEWS</span>
        </a>
        <form className="search-box" onSubmit={submitSearch}>
          <button type="submit" aria-label={copy.submitSearch} title={copy.search}>
            <Search size={17} />
          </button>
          <input
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            placeholder={copy.searchPlaceholder}
            aria-label={copy.search}
          />
          {draftQuery && (
            <button type="button" onClick={() => { setDraftQuery(""); setQuery(""); }} aria-label={copy.clearSearch}>
              <X size={15} />
            </button>
          )}
        </form>
        <div className="top-actions">
          <LocaleToggle />
          <span className="live-pill"><CircleDot size={12} /> LIVE</span>
          <button className="icon-button" aria-label={copy.notifications}><Bell size={19} /></button>
        </div>
      </header>

      <div className="page-grid">
        <aside className={`left-rail ${mobileMenuOpen ? "is-open" : ""}`}>
          <div className="mobile-rail-head">
            <span>{copy.channels}</span>
            <button className="icon-button" onClick={() => setMobileMenuOpen(false)} aria-label={copy.closeMenu}><X size={18} /></button>
          </div>
          <nav className="primary-nav" aria-label={copy.channelNav}>
            {TAB_CONFIG.map(({ id, icon: Icon }) => (
              <button
                key={id}
                className={activeTab === id ? "active" : ""}
                onClick={() => { setActiveTab(id); setMobileMenuOpen(false); }}
              >
                <Icon size={18} />
                <span>{tabLabels[id]}</span>
                {id === "latest" && <span className="nav-count">{events.length}</span>}
              </button>
            ))}
          </nav>

          <section className="rail-section">
            <div className="section-kicker">{copy.interests}</div>
            <div className="interest-list">
              {interestOptions.slice(0, 10).map((interest) => (
                <button
                  key={interest}
                  className={selectedInterests.includes(interest) ? "selected" : ""}
                  onClick={() => toggleInterest(interest)}
                >
                  <span className="interest-dot" />
                  <span>{interest}</span>
                  {selectedInterests.includes(interest) && <span className="following-check">✓</span>}
                </button>
              ))}
            </div>
          </section>

          <div className="rail-footer">
            <Zap size={15} />
            <span>{copy.railFooter}</span>
          </div>
        </aside>

        {mobileMenuOpen && <button className="rail-backdrop" onClick={() => setMobileMenuOpen(false)} aria-label={copy.closeMenu} />}

        <main className="main-feed">
          <section className="feed-heading">
            <div>
              <p className="eyebrow">GLOBAL GAME INTELLIGENCE</p>
              <h1>{tabLabels[activeTab]} {copy.intelligence}</h1>
              <p>{activeTab === "following" ? copy.followingDescription : copy.latestDescription}</p>
            </div>
            <button className={`filter-button ${filtersOpen ? "active" : ""}`} onClick={() => setFiltersOpen((value) => !value)}>
              <Filter size={16} /> {copy.filter}
              {(platform || query) && <span className="filter-count">{Number(Boolean(platform)) + Number(Boolean(query))}</span>}
            </button>
          </section>

          {filtersOpen && (
            <section className="filter-panel">
              <div>
                <label htmlFor="platform-filter">{copy.platform}</label>
                <select id="platform-filter" value={platform} onChange={(event) => setPlatform(event.target.value)}>
                  <option value="">{copy.allPlatforms}</option>
                  {facets.platforms.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.count})</option>)}
                </select>
              </div>
              <button onClick={() => { setPlatform(""); setDraftQuery(""); setQuery(""); }}>{copy.resetFilters}</button>
            </section>
          )}

          {activeTab === "following" && selectedInterests.length === 0 && (
            <section className="onboarding-card">
              <div className="onboarding-icon"><Sparkles size={22} /></div>
              <div>
                <strong>{copy.onboardingTitle}</strong>
                <p>{copy.onboardingBody}</p>
                <div className="interest-chips">
                  {interestOptions.slice(0, 8).map((interest) => (
                    <button key={interest} onClick={() => toggleInterest(interest)}>+ {interest}</button>
                  ))}
                </div>
              </div>
            </section>
          )}

          <div className="feed-status-row">
            <span><span className="pulse-dot" /> {loading ? copy.syncing : `${visibleEvents.length} ${copy.events}`}</span>
            <button onClick={() => { void loadFeed(); }} disabled={loading}><RefreshCw size={14} className={loading ? "spinning" : ""} /> {copy.refresh}</button>
          </div>

          {error && (
            <section className="error-card">
              <strong>{copy.apiErrorTitle}</strong>
              <p>{error}</p>
              <code>{API_BASE_URL}</code>
              <button onClick={() => { void loadFeed(); }}>{copy.retry}</button>
            </section>
          )}

          {!error && loading && events.length === 0 && (
            <div className="skeleton-stack" aria-label={copy.loading}>
              {[0, 1, 2].map((item) => <div className="story-skeleton" key={item}><span /><span /><span /></div>)}
            </div>
          )}

          {!error && !loading && visibleEvents.length === 0 && (
            <section className="empty-state">
              <div><Newspaper size={25} /></div>
              <h2>{events.length > 0 ? copy.noMatches : copy.collecting}</h2>
              <p>{events.length > 0 ? copy.noMatchesBody : copy.collectingBody}</p>
              {events.length > 0 && <button onClick={() => { setActiveTab("latest"); setPlatform(""); }}>{copy.viewAll}</button>}
            </section>
          )}

          <div className="story-list">
            {visibleEvents.map((story, index) => <StoryCard key={story.id} story={story} featured={index === 0} locale={locale} />)}
          </div>
        </main>

        <aside className="right-rail">
          <section className="radar-card">
            <div className="panel-title">
              <span><Flame size={16} /> {copy.sourceRadar}</span>
              <span className="status-dot" title={copy.realtime} />
            </div>
            <div className="radar-orbit" aria-hidden="true">
              <span className="orbit orbit-one" />
              <span className="orbit orbit-two" />
              <span className="radar-center"><Gamepad2 size={20} /></span>
              {sources.slice(0, 5).map((source, index) => (
                <span key={source.id} className={`radar-node node-${index + 1}`} title={source.name} />
              ))}
            </div>
            <div className="radar-stats">
              <div><strong>{sources.length}</strong><span>{copy.connectedSources}</span></div>
              <div><strong>{sources.filter((source) => source.enabled).length}</strong><span>{copy.enabledSources}</span></div>
            </div>
          </section>

          <section className="source-card">
            <div className="panel-title"><span>{copy.latestSources}</span><button>{copy.all}</button></div>
            <div className="source-list">
              {sources.slice(0, 6).map((source) => (
                <a key={source.id} href={source.homepageUrl ?? "#"} target={source.homepageUrl ? "_blank" : undefined} rel="noreferrer">
                  <span className="source-avatar">{source.name.slice(0, 2).toUpperCase()}</span>
                  <span><strong>{source.name}</strong><small>{source.itemCount} {copy.items}</small></span>
                  <ChevronRight size={15} />
                </a>
              ))}
              {sources.length === 0 && <p className="muted-copy">{copy.sourcesEmpty}</p>}
            </div>
          </section>

          <section className="beta-card">
            <p className="eyebrow">EARLY SIGNAL</p>
            <strong>{copy.learningTitle}</strong>
            <p>{copy.learningBody}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}

function StoryCard({ story, featured, locale }: { story: FeedEvent; featured: boolean; locale: DisplayLocale }) {
  const copy = APP_COPY[locale];
  const primarySource = story.sources[0];
  const href = story.canonicalUrl ?? primarySource?.url ?? null;
  const cardType = cardTypeForEvent(story);
  return (
    <article className={`story-card ${featured ? "featured" : ""}`}>
      <div className="story-meta">
        <span className={`type-badge type-${cardType}`}>{contentTypeLabel(cardType, locale)}</span>
        <span><Clock3 size={13} /> {timeAgo(story.publishedAt, locale)}</span>
        {story.sourceCount > 1 && <span className="multi-source">{story.sourceCount} {copy.sources}</span>}
      </div>
      <h2>{href ? <a href={href} target="_blank" rel="noreferrer">{story.title}</a> : story.title}</h2>
      {story.summary && <p className="story-summary">{story.summary}</p>}
      <div className="story-taxonomy">
        {[...story.games, ...story.platforms, ...story.tags].slice(0, 5).map((label) => <span key={label}>{label}</span>)}
      </div>
      <footer>
        <div className="source-stack">
          {story.sources.slice(0, 3).map((source) => <span key={source.id} className="mini-avatar" title={source.name}>{source.name.slice(0, 1)}</span>)}
          <span className="source-names">{story.sources.length > 0 ? story.sources.map((source) => source.name).slice(0, 2).join(" · ") : copy.sourcePending}</span>
        </div>
        {href && <a className="read-link" href={href} target="_blank" rel="noreferrer">{copy.readOriginal} <ExternalLink size={13} /></a>}
      </footer>
    </article>
  );
}

function cardTypeForEvent(story: FeedEvent): string {
  if (story.eventType === "review" || story.eventType === "system_update") {
    return story.eventType;
  }
  if (story.eventType === "patch" || story.eventType === "patch_notes") {
    return "patch";
  }
  return story.contentType;
}

export default App;
